import { createContext, use, useEffect, useMemo, type ReactNode, type RefObject } from 'react';
import { useInView } from './use-in-view';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';

const EASE = {
	inOut: '0.42 0 0.58 1',
	in: '0.42 0 1 1',
	out: '0 0 0.58 1',
	/** Quick off the mark, still accelerating when it leaves the card. */
	launch: '0.3 0.2 0.8 0.6',
} as const;

/** `ease` shapes the segment that ENDS at this key. */
export type Key = readonly [seconds: number, value: string | number, ease?: keyof typeof EASE];
export type Keys = readonly [Key, ...Key[]];

/** `enabled` is off under reduced motion: every track renders nothing and the drawing keeps its base values. */
const Timeline = createContext({ seconds: 1, enabled: false, once: false });

/** One loop length per drawing: every track shares it, so SMIL keeps them in step without a script. */
/** `once` plays the tracks a single time and freezes on their last values; pair it with `usePlayOnceInView`. */
export function SmilTimeline(props: { seconds: number; once?: boolean; children: ReactNode }) {
	const enabled = !usePrefersReducedMotion();
	const once = props.once ?? false;
	const value = useMemo(() => ({ seconds: props.seconds, enabled, once }), [props.seconds, enabled, once]);
	return <Timeline value={value}>{props.children}</Timeline>;
}

export function useSmilTimeline() {
	return use(Timeline);
}

/** Long enough for a carousel's snap to finish, so a slide scrolling away is not seen snapping back. */
const RESET_DELAY = 500;

/**
 * SMIL starts on page load, so a one-shot below the fold would finish unseen: this holds it on its first
 * frame until it scrolls into view, then plays it from the start. `active` replays it each time it turns true.
 * `holdMs` keeps that first frame on screen a moment longer, so the viewer sees where it starts from.
 */
export function usePlayOnceInView(ref: RefObject<SVGSVGElement | null>, active = true, holdMs = 0) {
	const inView = useInView(ref);
	useEffect(() => {
		const svg = ref.current;
		if (!svg) return;
		const rewind = () => {
			svg.pauseAnimations();
			svg.setCurrentTime(0);
		};
		if (inView && active) {
			rewind();
			const timer = setTimeout(() => svg.unpauseAnimations(), holdMs);
			return () => clearTimeout(timer);
		}
		if (!inView) return rewind();
		const timer = setTimeout(rewind, RESET_DELAY);
		return () => clearTimeout(timer);
	}, [ref, inView, active, holdMs]);
}

/** A looping timeline keeps ticking off-screen: this holds it while it is out of view and resumes it on return. */
export function usePauseOutOfView(ref: RefObject<SVGSVGElement | null>) {
	useEffect(() => {
		const svg = ref.current;
		if (!svg) return;
		const observer = new IntersectionObserver(([entry]) => {
			if (entry?.isIntersecting) svg.unpauseAnimations();
			else svg.pauseAnimations();
		});
		observer.observe(svg);
		return () => observer.disconnect();
	}, [ref]);
}

function repeat(once: boolean) {
	return once ? { fill: 'freeze' } : { repeatCount: 'indefinite' };
}

function track(keys: Keys, loop: number) {
	const first = keys[0];
	const last = keys.at(-1) ?? first;
	const lead: Key[] = first[0] > 0 ? [[0, first[1]]] : [];
	const tail: Key[] = last[0] < loop ? [[loop, last[1]]] : [];
	const all = [...lead, ...keys, ...tail];
	return {
		dur: `${loop}s`,
		keyTimes: all.map(([t]) => (t / loop).toFixed(4)).join(';'),
		values: all.map(([, value]) => value).join(';'),
		keySplines: all
			.slice(1)
			.map(([, , ease]) => EASE[ease ?? 'inOut'])
			.join(';'),
	};
}

export function Animate(props: { attr: string; keys: Keys }) {
	const timeline = useSmilTimeline();
	if (!timeline.enabled) return null;
	return (
		<animate
			attributeName={props.attr}
			{...repeat(timeline.once)}
			calcMode="spline"
			{...track(props.keys, timeline.seconds)}
		/>
	);
}

/** A second transform on one element needs `additive`, or it replaces the first. */
export function AnimateTransform(props: {
	type: 'translate' | 'rotate' | 'scale' | 'skewY';
	keys: Keys;
	additive?: boolean;
}) {
	const timeline = useSmilTimeline();
	if (!timeline.enabled) return null;
	return (
		<animateTransform
			attributeName="transform"
			type={props.type}
			additive={props.additive ? 'sum' : undefined}
			{...repeat(timeline.once)}
			calcMode="spline"
			{...track(props.keys, timeline.seconds)}
		/>
	);
}
